<template>
  <div v-if="visible" ref="dropdownElement" class="search-dropdown" data-dropdown @focusout="hideSearchBar">
    <VcScrollbar class="search-dropdown__sidebar">
      <div>
        <!-- Results -->
        <template v-if="isExistResults">
          <!-- Search history -->
          <section v-if="searchHistoryQueries.length && !searchHistoryLoading">
            <header class="px-5 pb-2 pt-4 text-sm text-neutral">
              {{ $t("shared.layout.search_bar.suggestions_and_history_label") }}
            </header>

            <div class="flex gap-5 px-5 pb-3 text-sm">
              <ul class="w-full">
                <li v-for="query in searchHistoryQueries" :key="query" class="w-full py-1">
                  <router-link
                    :to="getSearchRoute(query)"
                    @click="hideSearchBar"
                    class="flex justify-between items-center"
                  >
                    <span v-html-safe="highlightSearchPhrase(query, trimmedSearchPhrase)" />

                    <VcIcon name="history" size="md" />
                  </router-link>
                </li>
              </ul>
            </div>
          </section>

          <!-- Suggestions -->
          <section v-if="suggestions.length">
            <header class="px-5 pb-2 pt-4 text-sm text-neutral">
              {{ $t("shared.layout.search_bar.suggestions_label") }}
            </header>

            <div class="flex gap-5 px-5 pb-3 text-sm">
              <ul>
                <li v-for="suggestion in suggestions" :key="suggestion.text">
                  <router-link :to="getSearchRoute(suggestion.text)" class="block py-1" @click="hideSearchBar">
                    <span v-html-safe="highlightSearchPhrase(suggestion.label, trimmedSearchPhrase)" />
                  </router-link>
                </li>
              </ul>
            </div>
          </section>

          <!-- Categories -->
          <section v-if="categories.length">
            <header class="px-5 pb-2 text-sm text-neutral">
              {{ $t("shared.layout.search_bar.categories_label") }}
            </header>

            <div class="px-5 pb-3 text-sm">
              <ul v-for="(column, index) in categoriesColumns" :key="index">
                <li v-for="category in column" :key="category.name">
                  <router-link :to="categoriesRoutes[category.id]" class="block py-1" @click="hideSearchBar">
                    <span v-html-safe="highlightSearchPhrase(category.name, trimmedSearchPhrase)" />
                  </router-link>
                </li>
              </ul>
            </div>
          </section>

          <!-- Products -->
          <section v-if="products.length" class="pb-4">
            <header class="px-5 pb-2 text-sm text-neutral">
              {{ $t("shared.layout.search_bar.products_label") }}
            </header>

            <div class="grid grid-cols-1 gap-y-0.5 p-3">
              <InnoSearchBarProductCard
                v-for="product in products"
                :key="product.id"
                :product="product"
                :search-phrase="trimmedSearchPhrase"
                @link-click="hideSearchBar()"
              />
            </div>
          </section>

          <!-- Actions -->
          <section
            v-if="total && trimmedSearchPhrase"
            class="sticky bottom-0 mt-0.5 border-t border-neutral-100 bg-additional-50 px-5 py-3"
          >
            <VcButton size="sm" @click="goToSearchResultsPage">
              {{ $t("shared.layout.search_bar.view_all_results_button", { total }) }}
            </VcButton>
          </section>
        </template>

        <template v-else-if="loading">
          <VcLoaderOverlay />
        </template>

        <!-- Not found -->
        <div v-else-if="!loading && trimmedSearchPhrase.length > 2" class="my-16 text-center">
          <VcIcon name="search-not-found" class="mr-5 inline-block !h-12 !w-12 fill-primary" />

          <i18n-t class="inline-block" keypath="shared.layout.search_bar.no_results" tag="p">
            <template #keyword>
              <strong>{{ trimmedSearchPhrase }}</strong>
            </template>
          </i18n-t>
        </div>
      </div>
    </VcScrollbar>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn, useBreakpoints } from "@vueuse/core";
import { pickBy } from "lodash";
import { computed, onMounted, ref, toRefs, toValue, watch } from "vue";
import { useRouter } from "vue-router";
import { useCategoriesRoutes, useRouteQueryParam, useThemeContext, useAnalytics } from "@/core/composables";
import { useHistoricalEvents } from "@/core/composables/useHistoricalEvents";
import { BREAKPOINTS } from "@/core/constants";
import { QueryParamName } from "@/core/enums";
import { ROUTES } from "@/router/routes/constants";
import { useSearchBar } from "@/shared/layout/composables/useSearchBar";
import { highlightSearchPhrase } from "@/core/utilities";
import type { GetSearchResultsParamsType } from "@/core/api/graphql/catalog";
import type { Product } from "@/core/api/graphql/types";
import type { RouteLocationRaw } from "vue-router";
import type { Category } from "@/core/api/graphql/types";
import InnoSearchBarProductCard from "../../search-bar/innovadis/inno-search-bar-product-card.vue";

interface IProps {
  visible: boolean;
  searchPhrase: string;
  filterExpression?: string;
  categoriesFilterExpression?: string;
  isCategoryScope?: boolean;
}

interface IEmits {
  (e: "hide"): void;
  (e: "productSelect", product: Product): void;
  (e: "update:searchPhrase", value: string): void;
}

const emit = defineEmits<IEmits>();
const props = defineProps<IProps>();

const { visible } = toRefs(props);

const PRODUCTS_LIMIT = 8;
const CATEGORIES_LIMIT = 5;
const PAGES_LIMIT = 5;
const SUGGESTIONS_LIMIT = 5;

const CATEGORIES_ITEMS_PER_COLUMN = 4;
const categoriesColumns = computed<Array<Category[]>>(() => {
  const columnsCount: number = Math.ceil(categories.value.length / CATEGORIES_ITEMS_PER_COLUMN);

  return Array.from({ length: columnsCount }).map((_, index) => {
    const column: number = index + 1;
    return categories.value.slice((column - 1) * CATEGORIES_ITEMS_PER_COLUMN, column * CATEGORIES_ITEMS_PER_COLUMN);
  });
});

const isExistResults = computed(
  () => categories.value.length || products.value.length || suggestions.value.length || pages.value.length,
);

const router = useRouter();
const { themeContext } = useThemeContext();
const { saveSearchQuery, useGetSearchHistoryQuery } = useHistoricalEvents();
const { analytics } = useAnalytics();

const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller("md");

const dropdownElement = ref<HTMLElement | null>(null);
const searchInProgress = ref(false);

const SEARCH_BAR_DEBOUNCE_TIME = 200;

const { total, loading, pages, products, suggestions, categories, searchResults, maxSearchLength, hideSearchBar } =
  useSearchBar();

const categoriesRoutes = useCategoriesRoutes(categories);
const { result: searchHistory, load: loadSearchHistory, loading: searchHistoryLoading } = useGetSearchHistoryQuery();

const facetsParam = useRouteQueryParam<string>(QueryParamName.Facets);
const sortParam = useRouteQueryParam<string>(QueryParamName.Sort);

function getSearchRoute(phrase: string): RouteLocationRaw {
  const query = pickBy(
    {
      [QueryParamName.SearchPhrase]: phrase,
      [QueryParamName.Facets]: toValue(facetsParam),
      [QueryParamName.Sort]: toValue(sortParam),
    },
    (value) => !!value,
  );

  if (props.isCategoryScope) {
    return {
      path: router.currentRoute.value.path,
      query,
    } as RouteLocationRaw;
  }

  return {
    name: ROUTES.SEARCH.NAME,
    query,
  } as RouteLocationRaw;
}

const searchHistoryQueries = computed(() => searchHistory.value?.searchHistory?.queries ?? []);

const trimmedSearchPhrase = computed(() => props.searchPhrase.trim());

const searchBarListProperties = computed(() => ({
  search_term: trimmedSearchPhrase.value,
  item_list_id: "search_bar",
  item_list_name: `Search phrase '${trimmedSearchPhrase.value}'`,
}));

async function searchAndShowDropdownResults(): Promise<void> {
  const { search_product_phrase_suggestions_enabled, search_static_content_suggestions_enabled } =
    themeContext.value.settings;

  if (trimmedSearchPhrase.value.length > maxSearchLength.value || trimmedSearchPhrase.value.length === 0) {
    return;
  }

  const params: GetSearchResultsParamsType = {
    keyword: trimmedSearchPhrase.value,
    filter: props.filterExpression,
    categoriesFilter: props.categoriesFilterExpression,
    categories: {
      itemsPerPage: CATEGORIES_LIMIT,
    },
    products: {
      itemsPerPage: PRODUCTS_LIMIT,
    },
  };

  if (search_product_phrase_suggestions_enabled) {
    params.productSuggestions = { suggestionsSize: SUGGESTIONS_LIMIT };
  }

  if (search_static_content_suggestions_enabled) {
    params.pages = { itemsPerPage: PAGES_LIMIT };
  }

  await searchResults(params);

  if (products.value.length) {
    analytics("viewItemList", products.value, searchBarListProperties.value);
  }
}

const searchProductsDebounced = useDebounceFn(searchAndShowDropdownResults, SEARCH_BAR_DEBOUNCE_TIME);

async function onSearchPhraseChanged() {
  if (trimmedSearchPhrase.value) {
    searchInProgress.value = true;
    await searchProductsDebounced();
    searchInProgress.value = false;
  } else {
    await loadSearchHistory();
  }
}

function goToSearchResultsPage(phrase?: string) {
  const searchQuery = phrase || trimmedSearchPhrase.value;

  emit("hide");

  const ifAlreadySearch = !!router.currentRoute.value.query[QueryParamName.SearchPhrase];

  if (ifAlreadySearch) {
    void router.replace(getSearchRoute(searchQuery));
  } else {
    void router.push(getSearchRoute(searchQuery));
  }

  if (searchQuery) {
    analytics("search", searchQuery, products.value, total.value);
  }
}

async function handleSearch() {
  await saveSearchQuery(trimmedSearchPhrase.value);

  goToSearchResultsPage();
}

watch(
  () => props.searchPhrase,
  () => {
    void onSearchPhraseChanged();
  },
);

watch(visible, (isVisible, wasVisible) => {
  if (isVisible && !wasVisible) {
    void onSearchPhraseChanged();
  }
});

onMounted(() => {
  void loadSearchHistory();

  if (trimmedSearchPhrase.value) {
    void onSearchPhraseChanged();
  }
});

defineExpose({
  dropdownElement,
  handleSearch,
});
</script>

<style lang="scss">
.search-dropdown {
  @apply pt-1 px-3.5 pb-3.5;

  @media (min-width: theme("screens.md")) {
    @apply flex gap-2.5 pb-3 max-h-[inherit] min-h-36;
  }

  @media (min-width: theme("screens.lg")) {
    @apply pt-4 ps-5 pe-2.5 pb-5 rounded-[--vc-radius] shadow-lg bg-additional-50;
  }

  &__sidebar {
    @media (min-width: theme("screens.md")) {
      @apply flex-shrink-0 w-[12rem] max-h-full pe-2.5;
    }

    @media (min-width: theme("screens.xl")) {
      @apply w-[15.875rem];
    }
  }

  &__suggestions {
    @apply mt-0.5;

    &:not(:first-child) {
      @apply mt-4;
    }
  }

  &__head {
    @apply text-xs font-bold text-neutral-600 uppercase;
  }

  &__list {
    @apply flex flex-col mt-1.5 border rounded-[--vc-radius] bg-neutral-50 px-2 py-2;
  }

  &__item {
    --vc-icon-color: theme("colors.secondary.700");

    @apply flex items-center gap-1 py-[0.325rem] px-2 text-sm text-[--link-color];

    &:hover {
      @apply text-[--link-hover-color];
    }
  }

  &__content {
    @apply relative mt-4;

    @media (min-width: theme("screens.md")) {
      @apply flex-1 mt-0 max-h-full pe-2.5;
    }

    &:empty {
      @apply hidden;
    }
  }

  &__products {
    @apply mt-1.5 border rounded-[--vc-radius] p-3;

    @media (width < theme("screens.sm")) {
      @apply space-y-5;
    }

    @media (min-width: theme("screens.sm")) {
      @apply grid grid-cols-2 gap-x-8 gap-y-5;
    }
  }

  &__text {
    word-break: break-word;
  }

  &__loader {
    @apply absolute inset-0 flex items-center justify-center bg-additional-50/40 pointer-events-none;
  }

  &__not-found {
    @apply flex items-center justify-center gap-5 h-full;
  }

  &__not-found-icon {
    @apply size-32;
  }

  &__not-found-content {
    @apply grow max-w-[29.5rem] space-y-3;
  }

  &__not-found-text {
    @apply text-base;
  }

  &__results {
    @apply min-h-32;
  }

  // transitions
  .crossfade-enter-active,
  .crossfade-leave-active {
    @apply transition-opacity duration-150 ease-in-out;
  }

  .crossfade-enter-from,
  .crossfade-leave-to {
    @apply opacity-0;
  }

  .fade-enter-active,
  .fade-leave-active {
    @apply transition-opacity duration-200 ease-in-out;
  }

  .fade-enter-from,
  .fade-leave-to {
    @apply opacity-0;
  }
}
</style>
